import { useCallback, useRef, useState } from 'react';
import {
  EventLocationType,
  EventStatus,
  EventType,
} from '@weave/schema-gen-ts/dist/schemas/schedule/v3/calendar_event.pb';
import { Provider } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/provider.pb';
import { DataSourcesQueries } from '@frontend/api-data-sources';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { CalendarEvent, CalendarGridDragStyles } from '@frontend/calendar-grid';
import { formatDate } from '@frontend/date';
import { getFullName } from '@frontend/name';
import { theme } from '@frontend/theme';
import {
  GridCalendarEvent,
  isGridAppointmentCalendarEvent,
  isGridDraggableEvent,
} from '../../components/calendar-view/types';
import { DATETIME_FORMATS } from '../../constants';
import { getTimeDiff } from '../../utils/date-helpers';
import { isUnassignedColumnId } from './calendar-grid-v3-event-builders';
import {
  calculateNewDateTime,
  findGridEventById,
  formatEventDateTimeForTracking,
  getTimezoneFromLocationMap,
  type DropPayload,
} from './use-appointment-drag-drop';
import { useGridAppointmentDragDropPendoTracking } from './use-appointment-drag-drop-pendo-tracking';

type DragDropState = {
  pendingDropPayload: DropPayload | null;
};

const initialDragDropState: DragDropState = {
  pendingDropPayload: null,
};

type UseCalendarGridV3EventDragDropParams = {
  /** Flat list of GridCalendarEvents from useCalendarGridV3Data — replaces calendarObject */
  events: GridCalendarEvent[];
  calendarDate: string;
  startHour?: number;
  sortedProviders?: (Provider & { fullName: string })[];
  onDropSuccess?: () => void;
  onDropError?: (error: unknown) => void;
};

/**
 * Hook to bridge CalendarGrid's event drag-and-drop callbacks to the V3 appointment update mutation.
 * Handles confirmation modal state and placement rules.
 */
export const useCalendarGridV3EventDragDrop = ({
  events,
  calendarDate,
  startHour = 0,
  sortedProviders,
  onDropSuccess,
  onDropError,
}: UseCalendarGridV3EventDragDropParams) => {
  const { isScheduleV3FlagEnabled } = SchedulerV3.Helpers.useGetSchedulerV3FeatureFlagDetails();
  const { isIntegratedOffice } = DataSourcesQueries.useGetAccessibleIntegrations();
  const { locationTimezoneMap } = SchedulerV3.Helpers.useScheduleLocationTimezoneShallowStore('locationTimezoneMap');
  const [dragDropState, setDragDropState] = useState<DragDropState>(initialDragDropState);
  const trackPendoEvent = useGridAppointmentDragDropPendoTracking(events);

  const clearDragPreviewRef = useRef<(() => void) | null>(null);

  const {
    invalidateListCalendarEvents,
    invalidateListCalendarEventsByAttendee,
    invalidateListScheduleActivity,
    invalidateListScheduleActivityHistory,
  } = SchedulerV3.Helpers.useSchedulerV3InvalidateQueries();

  const updateCalendarEventMutation = SchedulerV3.Mutations.useUpdateCalendarEventMutation();

  // Enable drag only for v3 core offices (not integrated)
  const isEventDraggingEnabled = isScheduleV3FlagEnabled && !isIntegratedOffice;

  const handleEventDragStart = useCallback(
    (eventId: string) => {
      const originalEvent = findGridEventById(events, eventId);
      trackPendoEvent('appointment_card_dragged', {
        eventId,
        sourceColumnEntityId: originalEvent?.entityId,
      });
    },
    [events, trackPendoEvent]
  );

  const handleEventDragEnd = useCallback(
    (
      eventId: string,
      targetColumnId: string,
      targetGroupId: string,
      newStartMinutes: number,
      clearPreview: () => void
    ) => {
      clearDragPreviewRef.current = clearPreview;
      // targetGroupId maps to locationId, targetColumnId maps to entityId
      const targetLocationId = targetGroupId;
      // The shared confirmation modal treats an empty entity id as unassigned
      const targetColumnEntityId = isUnassignedColumnId(targetColumnId) ? '' : targetColumnId;

      // Find the original event from our grid events list
      const originalEvent = findGridEventById(events, eventId);
      if (!originalEvent) {
        console.error('Could not find original event for drag-drop');
        return;
      }

      // Determine source info from the original event's enriched fields
      const sourceColumnEntityId = originalEvent.entityId || '';
      const sourceLocationId = originalEvent.locationId || '';

      // Create pending drop payload to show confirmation modal.
      // The CalendarGrid widget intentionally keeps eventDragPreview alive after drag ends
      // so the drop-target overlay stays visible while the modal is open.
      const dropPayload: DropPayload = {
        eventId,
        sourceColumnEntityId,
        sourceLocationId,
        targetColumnEntityId,
        targetLocationId,
        newStartTimeMinutes: newStartMinutes,
      };

      setDragDropState({ pendingDropPayload: dropPayload });
    },
    [events]
  );

  const confirmDrop = useCallback(async () => {
    const { pendingDropPayload } = dragDropState;

    if (!pendingDropPayload) {
      setDragDropState(initialDragDropState);
      return;
    }

    const { eventId, targetColumnEntityId, newStartTimeMinutes, targetLocationId, sourceColumnEntityId } =
      pendingDropPayload;
    const locationTimezone = getTimezoneFromLocationMap(locationTimezoneMap, targetLocationId);

    const originalEvent = findGridEventById(events, eventId);

    if (!originalEvent) {
      console.error('Could not find original event for drag-drop update');
      setDragDropState(initialDragDropState);
      return;
    }

    // Only appointments are draggable — narrow to GridAppointmentCalendarEvent so
    // appointment-specific fields (patientId, eventStatus, referenceId, etc.) are accessible.
    // This mirrors the isEventDraggable guard and acts as a runtime safety net.
    if (!isGridDraggableEvent(originalEvent)) {
      console.error('Unexpected event type in drag-drop confirm:', originalEvent.eventType);
      setDragDropState(initialDragDropState);
      return;
    }

    const columnEntityEvent = events
      .filter(isGridAppointmentCalendarEvent)
      .find((e) => e.entityId === targetColumnEntityId);
    const targetProvider = sortedProviders?.find((p) => p.id === targetColumnEntityId);
    const targetEntityCalendarId = targetProvider?.calendarId || columnEntityEvent?.calendarId || '';
    const targetEntityName = targetProvider ? getFullName(targetProvider) : columnEntityEvent?.entityName || '';

    const durationMinutes = getTimeDiff(originalEvent.startHour ?? '', originalEvent.endHour ?? '', {
      format: 'hh:mm A',
      crossDay: true,
    });

    const { startDate, startTime, endDate, endTime } = calculateNewDateTime(
      calendarDate,
      newStartTimeMinutes,
      durationMinutes,
      startHour,
      locationTimezone
    );

    const newStartDateTime = formatEventDateTimeForTracking(startDate, startTime, 'UTC', locationTimezone);
    const newEndDateTime = formatEventDateTimeForTracking(endDate, endTime, 'UTC', locationTimezone);

    trackPendoEvent('appointment_card_drag_confirm_button_clicked', {
      eventId,
      sourceColumnEntityId,
      targetColumnEntityId,
      newStartDateTime,
      newEndDateTime,
    });

    try {
      const payload = {
        eventId,
        attendeeId: originalEvent.patientId || '',
        attendeeStatus: originalEvent.eventStatus || EventStatus.UNCONFIRMED,
        eventType: EventType.APPOINTMENT,
        locationType: EventLocationType.OFFICE,
        organizerId: targetColumnEntityId,
        organizerCalendarId: targetEntityCalendarId,
        startDate,
        startTime,
        endDate,
        endTime,
        startTimezone: locationTimezone || '',
        endTimezone: locationTimezone || '',
        referenceId: originalEvent.referenceId || '',
        referenceTypeId: originalEvent.appointmentTypeId || '',
        details: {
          dur: durationMinutes,
          orNa: targetEntityName,
          rTyNa: originalEvent.appointmentTypeName || '',
          col: originalEvent.backgroundColor || originalEvent.displayOptions?.backgroundColor || '',
        },
        existingStartDate: formatDate(originalEvent.startDate ?? '', DATETIME_FORMATS.ISO_DATE),
      };

      await updateCalendarEventMutation.mutateAsync(payload);
      invalidateListCalendarEvents({
        locationId: targetLocationId,
        ignoreDate: true,
      });
      invalidateListCalendarEventsByAttendee(targetLocationId);
      invalidateListScheduleActivity({ locationId: targetLocationId });
      invalidateListScheduleActivityHistory({ locationId: targetLocationId, referenceId: payload.referenceId });

      onDropSuccess?.();
    } catch (error) {
      console.error('Failed to update calendar event:', error);
      onDropError?.(error);
    } finally {
      setDragDropState(initialDragDropState);
      clearDragPreviewRef.current?.();
    }
  }, [
    dragDropState,
    events,
    sortedProviders,
    locationTimezoneMap,
    calendarDate,
    startHour,
    updateCalendarEventMutation,
    invalidateListCalendarEvents,
    invalidateListCalendarEventsByAttendee,
    invalidateListScheduleActivity,
    invalidateListScheduleActivityHistory,
    onDropSuccess,
    onDropError,
    trackPendoEvent,
  ]);

  const cancelDrop = useCallback(() => {
    setDragDropState(initialDragDropState);
    clearDragPreviewRef.current?.();
  }, []);

  const isEventDraggable = useCallback((event: CalendarEvent) => {
    // Only appointments are draggable
    return isGridDraggableEvent(event as GridCalendarEvent);
  }, []);

  const dragStyles: CalendarGridDragStyles = {
    // Source card: visible shell with dashed border, all inner content hidden
    eventCardOverlay: {
      opacity: 1,
      border: `1px dashed ${theme.colors.primary50}`,
      boxShadow: theme.shadows.light,
      borderRadius: theme.borderRadius.small,
      pointerEvents: 'none',
      backgroundColor: 'transparent',
      cursor: 'grabbing',
      // Hide rendered card content — show only the placeholder shell shape
      '& > *': { opacity: 0 },
    },
    // Drop-target placeholder: plain shape rendered as a bare <div> by the widget,
    // matching EventTimelineWrapper's dropTargetPlaceholder in the timeline view.
    eventDropOverlay: {
      backgroundColor: theme.colors.primary10,
      border: `1px solid ${theme.colors.primary20}`,
      borderRadius: theme.borderRadius.small,
      left: 0,
      right: 0,
      '& > *': { opacity: 0 },
    },
  };

  return {
    onEventDragStart: isEventDraggingEnabled ? handleEventDragStart : undefined,
    onEventDragEnd: isEventDraggingEnabled ? handleEventDragEnd : undefined,
    isEventDraggable: isEventDraggingEnabled ? isEventDraggable : undefined,
    dragStyles: isEventDraggingEnabled ? dragStyles : undefined,
    dragDropState,
    confirmDrop,
    cancelDrop,
  };
};
